/**
 * Custom Prompts Management Page
 * Manage workflow templates with hybrid tool discovery
 */

import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Plus, Trash2, Play, Edit2, Save, X, Tag, Workflow, CheckCircle, Wrench } from 'lucide-react';
import { PromptToolRelationsModal } from '../components/prompts';

interface Prompt {
  id: string;
  name: string;
  description: string | null;
  template: string;
  arguments: any[];
  workflow: any[];
  auto_execute: boolean;
  tags: string[];
  related_tools: string[];
  use_count: number;
  created_at: string;
  updated_at: string;
}

export function Prompts() {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showToolRelationsModal, setShowToolRelationsModal] = useState(false);
  const [selectedPromptForTools, setSelectedPromptForTools] = useState<Prompt | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: '',
    tags: '',
    workflow: [] as any[],
    auto_execute: false,
  });

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const data = await api.prompts.list();
      setPrompts(data.prompts || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a prompt name');
      return;
    }

    if (!formData.template.trim()) {
      setError('Please enter a template');
      return;
    }

    try {
      setError(null);
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);

      await api.prompts.create({
        name: formData.name,
        description: formData.description || undefined,
        template: formData.template,
        tags,
        workflow: formData.workflow.length > 0 ? formData.workflow : undefined,
        auto_execute: formData.auto_execute,
      });

      setSuccess('Prompt created successfully!');
      setShowCreateModal(false);
      resetForm();
      await loadPrompts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingPrompt) return;

    try {
      setError(null);
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);

      await api.prompts.update(editingPrompt.id, {
        name: formData.name,
        description: formData.description || undefined,
        template: formData.template,
        tags,
        workflow: formData.workflow.length > 0 ? formData.workflow : undefined,
        auto_execute: formData.auto_execute,
      });

      setSuccess('Prompt updated successfully!');
      setEditingPrompt(null);
      resetForm();
      await loadPrompts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete prompt "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.prompts.delete(id);
      setSuccess('Prompt deleted successfully!');
      await loadPrompts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const startEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      template: prompt.template,
      tags: prompt.tags.join(', '),
      workflow: prompt.workflow || [],
      auto_execute: prompt.auto_execute,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      template: '',
      tags: '',
      workflow: [],
      auto_execute: false,
    });
  };

  const cancelEdit = () => {
    setEditingPrompt(null);
    setShowCreateModal(false);
    resetForm();
    setError(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Prompts</h1>
          <p className="text-gray-600 mt-1">
            Create workflow templates with automatic tool discovery
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Prompt
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Prompts List */}
      {prompts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <Workflow className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first workflow template to get started
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Create Prompt
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {prompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              {editingPrompt?.id === prompt.id ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                    </label>
                    <textarea
                      value={formData.template}
                      onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="documentation, search, n8n"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.auto_execute}
                      onChange={(e) => setFormData({ ...formData, auto_execute: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-700">Auto-execute workflow</label>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdate}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{prompt.name}</h3>
                      {prompt.description && (
                        <p className="text-gray-600 mt-1">{prompt.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedPromptForTools(prompt);
                          setShowToolRelationsModal(true);
                        }}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Manage Tool Relations"
                      >
                        <Wrench className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => startEdit(prompt)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(prompt.id, prompt.name)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {prompt.template}
                    </pre>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {prompt.tags.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        <span>{prompt.tags.join(', ')}</span>
                      </div>
                    )}

                    {prompt.related_tools && prompt.related_tools.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Workflow className="w-4 h-4" />
                        <span>{prompt.related_tools.length} related tool(s)</span>
                      </div>
                    )}

                    {prompt.use_count > 0 && (
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        <span>Used {prompt.use_count} time(s)</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Create New Prompt</h2>
                <button
                  onClick={cancelEdit}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="search_n8n_docs"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Search n8n documentation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template *
                </label>
                <textarea
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  placeholder="Search n8n documentation for: {{topic}}"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use {'{{'} {'}}'} for variables, e.g., {'{{'} topic {'}}'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="documentation, search, n8n"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.auto_execute}
                  onChange={(e) => setFormData({ ...formData, auto_execute: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">Auto-execute workflow</label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleCreate}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Save className="w-4 h-4" />
                  Create Prompt
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tool Relations Modal */}
      {selectedPromptForTools && (
        <PromptToolRelationsModal
          isOpen={showToolRelationsModal}
          onClose={() => {
            setShowToolRelationsModal(false);
            setSelectedPromptForTools(null);
          }}
          promptId={selectedPromptForTools.id}
          promptName={selectedPromptForTools.name}
        />
      )}
    </div>
  );
}

export default Prompts;
